<template>
  <div class="dc-page">
    <v-card class="mx-auto mb-5 mt-5" width="90%">
      <v-container fluid class="px-6 py-5">
        <div class="d-flex align-start flex-wrap">
          <div class="dc-header">
            <div class="text-h6">🔎 {{ $t('views.DataCheck.title') }}</div>
            <div class="body-2 grey--text text--darken-1 mt-1">{{ $t('views.DataCheck.subtitle') }}</div>
            <div v-if="approvedFile" class="caption grey--text text--darken-1 mt-3">
              <v-icon small class="mr-1">mdi-file-table-outline</v-icon>
              <span class="font-weight-medium">{{ approvedFile.name }}</span>
              <span> · {{ $t('views.DataCheck.uploadedBy', { name: approvedFile.uploadedBy, date: formatDate(approvedFile.dateUploaded) }) }}</span>
              <span v-if="approvedFile.approvedBy"> · {{ $t('views.DataCheck.templateApprovedBy', { name: approvedFile.approvedBy, date: formatDate(approvedFile.approvedDate) }) }}</span>
            </div>
          </div>
          <v-spacer></v-spacer>
          <div v-if="approvedFile" class="mt-1">
            <v-chip v-if="isApproved" color="green" text-color="white">
              <v-icon left small>mdi-check-decagram</v-icon>{{ $t('views.DataCheck.statusApproved') }}
            </v-chip>
            <v-chip v-else color="orange darken-1" outlined>
              <v-icon left small>mdi-timer-sand</v-icon>{{ $t('views.DataCheck.statusPending') }}
            </v-chip>
          </div>
        </div>
        <v-alert v-if="validationWarnings" type="info" text dense class="mt-4 mb-0">
          {{ $tc('views.DataCheck.validationWarnings', validationWarnings, { count: validationWarnings }) }}
          <a class="ml-1" @click="goToDataUpload">{{ $t('views.DataCheck.viewInDataUpload') }}</a>
        </v-alert>
      </v-container>
    </v-card>

    <!-- No approved template -->
    <v-card v-if="status === 'noTemplate'" class="mx-auto mb-5" width="90%">
      <div class="dc-empty">
        <v-icon size="64" color="grey lighten-1">mdi-table-eye</v-icon>
        <div class="text-h6 mt-4">{{ $t('views.DataCheck.noTemplateTitle') }}</div>
        <p class="body-2 grey--text text--darken-1 mt-2 mb-5">{{ $t('views.DataCheck.noTemplateText') }}</p>
        <v-btn color="#212121" class="white--text" @click="goToDataUpload">
          <v-icon left color="primary">mdi-table-arrow-up</v-icon>{{ $t('views.DataCheck.goToDataUpload') }}
        </v-btn>
      </div>
    </v-card>

    <!-- Loading -->
    <v-card v-else-if="status === 'loading'" class="mx-auto mb-5" width="90%">
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
      <div class="dc-empty">
        <v-icon size="56" color="primary">mdi-chart-timeline-variant-shimmer</v-icon>
        <div class="text-h6 mt-4">{{ $t('views.DataCheck.loadingTitle') }}</div>
        <p class="body-2 grey--text text--darken-1 mt-2 mb-0">{{ slowLoading ? $t('views.DataCheck.loadingSlowText') : $t('views.DataCheck.loadingText') }}</p>
      </div>
      <v-row class="px-6 pb-6">
        <v-col v-for="n in 4" :key="n" cols="12" md="6">
          <v-skeleton-loader type="image" height="160"></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-card>

    <!-- Error -->
    <v-card v-else-if="status === 'error'" class="mx-auto mb-5" width="90%">
      <div class="dc-empty">
        <v-icon size="56" color="red lighten-2">mdi-alert-circle-outline</v-icon>
        <div class="text-h6 mt-4">{{ $t('views.DataCheck.errorTitle') }}</div>
        <p class="body-2 grey--text text--darken-1 mt-2 mb-5">{{ errorText }}</p>
        <v-btn v-if="canRetry" color="primary" outlined @click="fetchSummary">
          <v-icon left>mdi-refresh</v-icon>{{ $t('views.DataCheck.retry') }}
        </v-btn>
      </div>
    </v-card>

    <template v-else-if="status === 'ready'">
      <!-- Tiles -->
      <div class="mx-auto mb-5 dc-tiles" style="width: 90%">
        <v-card v-for="tile in tiles" :key="tile.key" class="dc-tile" outlined>
          <div class="dc-tile__label">
            <v-icon small class="mr-1" :color="tile.iconColor || 'grey'">{{ tile.icon }}</v-icon>{{ tile.label }}
          </div>
          <div class="dc-tile__value" :class="tile.valueClass">{{ tile.value }}</div>
        </v-card>
      </div>

      <v-card class="mx-auto mb-5" width="90%">
        <v-row no-gutters>
          <v-col cols="12" md="3" class="dc-nav">
            <v-list dense nav class="dc-nav__list">
              <template v-for="group in navGroups">
                <v-subheader :key="`h-${group.key}`" class="dc-nav__header">{{ $t(`views.DataCheck.${group.labelKey}`) }}</v-subheader>
                <v-list-item
                  v-for="item in group.items"
                  :key="item.id"
                  :input-value="selectedId === item.id"
                  color="primary"
                  @click="selectedId = item.id"
                >
                  <v-list-item-content>
                    <v-list-item-title class="dc-nav__title">{{ item.title }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="d-flex flex-row align-center my-0">
                    <v-chip v-if="item.warnings" x-small color="orange lighten-4" text-color="orange darken-4" class="mr-1">
                      <v-icon x-small left>mdi-alert</v-icon>{{ item.warnings }}
                    </v-chip>
                    <v-chip x-small>{{ item.count }}</v-chip>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>
          </v-col>
          <v-col cols="12" md="9" class="pa-5">
            <data-check-sheet-panel
              v-if="selectedSheet"
              :sheet="selectedSheet"
              :flags="flagsBySheet[selectedSheet.name]"
              :range="summary.range"
              :kpi-range="summary.kpiRange"
              :events="summary.events"
              :color="primaryColor"
            ></data-check-sheet-panel>
            <div v-else-if="selectedId === SPECIAL_DATES">
              <div class="text-h6 mb-3">{{ $t('views.DataCheck.groupSpecialDates') }}</div>
              <v-data-table
                :headers="eventHeaders"
                :items="eventItems"
                dense
                :items-per-page="15"
                :no-data-text="$t('views.DataCheck.specialDatesEmpty')"
              ></v-data-table>
              <div v-if="summary.eventsTruncated" class="caption grey--text mt-2">{{ $t('views.DataCheck.eventsTruncated') }}</div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Approval -->
      <v-card class="mx-auto mb-3" width="90%">
        <v-container fluid class="px-6 py-5">
          <div v-if="isApproved" class="d-flex align-center flex-wrap">
            <v-icon large color="green" class="mr-3">mdi-check-decagram</v-icon>
            <div>
              <div class="subtitle-1 font-weight-medium">{{ $t('views.DataCheck.approvedTitle') }}</div>
              <div class="body-2 grey--text text--darken-1">{{ $t('views.DataCheck.approvedText', { name: approvedFile.dataCheckApprovedBy, date: formatDate(approvedFile.dataCheckApprovedDate) }) }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn text small :loading="approving" @click="setApproval(false)">{{ $t('views.DataCheck.markAsPending') }}</v-btn>
          </div>
          <div v-else class="d-flex align-center flex-wrap dc-approval">
            <div class="dc-approval__text">
              <div class="subtitle-1 font-weight-medium">{{ $t('views.DataCheck.approvalTitle') }}</div>
              <div class="body-2 grey--text text--darken-1">{{ $t('views.DataCheck.approvalText') }}</div>
              <v-checkbox v-model="reviewed" :label="$t('views.DataCheck.approvalCheckbox')" hide-details dense class="mt-3"></v-checkbox>
            </div>
            <v-spacer></v-spacer>
            <v-btn color="#212121" class="white--text" :disabled="!reviewed || approving" :loading="approving" @click="setApproval(true)">
              <v-icon left color="primary">mdi-thumb-up</v-icon>{{ $t('views.DataCheck.approveButton') }}
            </v-btn>
          </div>
        </v-container>
      </v-card>
      <div class="mx-auto mb-6 caption grey--text" style="width: 90%">
        {{ $t('views.DataCheck.generatedAt', { date: formatDate(summary.generatedAt) }) }}
        <span v-if="summary.truncated"> · {{ $t('views.DataCheck.partialNotice') }}</span>
      </div>
    </template>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import DataCheckSheetPanel from '@/components/dataCheck/DataCheckSheetPanel'
import { computeSheetFlags, frequencyKey, warningCount } from '@/utils/dataCheck'

const GROUPS = [
  { key: 'kpi', labelKey: 'groupKpi' },
  { key: 'own', labelKey: 'groupOwn' },
  { key: 'competition', labelKey: 'groupCompetition' },
  { key: 'context', labelKey: 'groupContext' }
]
const SPECIAL_DATES = '__specialDates'
const DATA_SHEETS = 21
const SLOW_LOADING_MS = 20000
const FREQUENCY_KEYS = {
  daily: 'frequencyDaily',
  weekly: 'frequencyWeekly',
  monthly: 'frequencyMonthly',
  quarterly: 'frequencyQuarterly',
  yearly: 'frequencyYearly',
  irregular: 'frequencyIrregular'
}

// Summaries already read in this session, so coming back to the page doesn't fetch them again
const summaryCache = {}

export default {
  data () {
    return {
      status: 'waiting',
      summary: null,
      errorReason: null,
      slowLoading: false,
      slowTimer: null,
      selectedId: null,
      reviewed: false,
      approving: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      requestId: 0,
      SPECIAL_DATES: SPECIAL_DATES
    }
  },
  computed: {
    ...mapGetters('clients', [
      'currentClient',
      'currentClientDataFiles',
      'currentClientDataFilesLoaded'
    ]),
    approvedFile () {
      return _.find(this.currentClientDataFiles, dataFile => dataFile.approved) || null
    },
    approvedFileName () {
      return this.approvedFile ? this.approvedFile.name : null
    },
    isApproved () {
      return !!(this.approvedFile && this.approvedFile.dataCheckApproved)
    },
    validationWarnings () {
      const validation = this.approvedFile && this.approvedFile.automaticValidation
      return validation && validation.warnings ? validation.warnings.length : 0
    },
    primaryColor () {
      return this.$vuetify.theme.currentTheme.primary
    },
    flagsBySheet () {
      const flags = {}
      if (!this.summary) return flags
      this.summary.sheets.forEach(sheet => {
        flags[sheet.name] = computeSheetFlags(sheet, this.summary.kpiRange)
      })
      return flags
    },
    // Series whose flags include at least one warning, per sheet
    warningsBySheet () {
      const counts = {}
      Object.keys(this.flagsBySheet).forEach(name => {
        counts[name] = this.flagsBySheet[name].filter(flags => warningCount(flags) > 0).length
      })
      return counts
    },
    navGroups () {
      if (!this.summary) return []
      const groups = GROUPS.map(group => ({
        key: group.key,
        labelKey: group.labelKey,
        items: this.summary.sheets
          .filter(sheet => sheet.group === group.key)
          .map(sheet => ({
            id: sheet.name,
            title: sheet.name,
            count: this.variableCount(sheet),
            warnings: this.warningsBySheet[sheet.name] || 0
          }))
      })).filter(group => group.items.length)
      if (this.summary.events.length) {
        groups.push({
          key: 'specialDates',
          labelKey: 'groupSpecialDates',
          items: [{ id: SPECIAL_DATES, title: '14. Special Dates & Outliers', count: this.summary.events.length, warnings: 0 }]
        })
      }
      return groups
    },
    selectedSheet () {
      if (!this.summary) return null
      return _.find(this.summary.sheets, sheet => sheet.name === this.selectedId) || null
    },
    tiles () {
      const summary = this.summary
      const kpiSheet = _.find(summary.sheets, sheet => sheet.group === 'kpi') || summary.sheets[0]
      const flagged = Object.values(this.warningsBySheet).reduce((total, count) => total + count, 0)
      const variables = summary.sheets.reduce((total, sheet) => total + this.variableCount(sheet), 0)
      const sheetsWithData = summary.sheets.length + (summary.events.length ? 1 : 0)
      return [
        {
          key: 'range',
          icon: 'mdi-calendar-range',
          label: this.$t('views.DataCheck.tileDateRange'),
          valueClass: 'dc-tile__value--nowrap',
          value: summary.range ? `${this.formatMonth(summary.range.start)} – ${this.formatMonth(summary.range.end)}` : '–'
        },
        {
          key: 'frequency',
          icon: 'mdi-calendar-sync',
          label: this.$t('views.DataCheck.tileFrequency'),
          value: kpiSheet ? this.$t(`views.DataCheck.${FREQUENCY_KEYS[frequencyKey(kpiSheet.g, kpiSheet.bucket)]}`) : '–'
        },
        {
          key: 'series',
          icon: 'mdi-chart-line',
          label: this.$t('views.DataCheck.tileSeries'),
          value: variables
        },
        {
          key: 'sheets',
          icon: 'mdi-table-multiple',
          label: this.$t('views.DataCheck.tileSheetsWithData'),
          value: this.$t('views.DataCheck.tileSheetsOf', { count: sheetsWithData, total: DATA_SHEETS })
        },
        {
          key: 'flagged',
          icon: flagged ? 'mdi-alert' : 'mdi-check-circle-outline',
          iconColor: flagged ? 'orange darken-1' : 'green',
          label: this.$t('views.DataCheck.tileFlagged'),
          value: flagged || this.$t('views.DataCheck.tileNone'),
          valueClass: flagged ? 'orange--text text--darken-2' : 'green--text'
        }
      ]
    },
    eventHeaders () {
      return [
        { text: this.$t('views.DataCheck.eventsHeaderStart'), value: 'start', width: 130 },
        { text: this.$t('views.DataCheck.eventsHeaderEnd'), value: 'end', width: 130 },
        { text: this.$t('views.DataCheck.eventsHeaderEvent'), value: 'name' },
        { text: this.$t('views.DataCheck.eventsHeaderDescription'), value: 'desc' }
      ]
    },
    eventItems () {
      return this.summary.events.map(event => ({
        start: moment.utc(event.s, 'YYYY-MM-DD').format('ll'),
        end: moment.utc(event.e, 'YYYY-MM-DD').format('ll'),
        name: event.name,
        desc: event.desc
      }))
    },
    errorText () {
      if (this.errorReason === 'permission') return this.$t('views.DataCheck.errorPermission')
      if (this.errorReason === 'not-approved') return this.$t('views.DataCheck.errorNotApproved')
      if (this.errorReason === 'disabled') return this.$t('views.DataCheck.errorDisabled')
      return this.$t('views.DataCheck.errorText')
    },
    canRetry () {
      return this.errorReason !== 'permission' && this.errorReason !== 'disabled'
    }
  },
  methods: {
    ...mapActions('clients', [
      'loadClientDataFiles',
      'getDataCheckSummary',
      'setDataCheckApproval'
    ]),
    variableCount (sheet) {
      return new Set(sheet.series.map(series => series.k.join('\u001f'))).size
    },
    formatDate (ms) {
      return ms ? moment(ms).format('ll') : '–'
    },
    formatMonth (dateKey) {
      return moment.utc(dateKey, 'YYYY-MM-DD').format('MMM YYYY')
    },
    goToDataUpload () {
      this.$router.push({ name: 'dataUpload' })
    },
    clearSlowTimer () {
      if (this.slowTimer) clearTimeout(this.slowTimer)
      this.slowTimer = null
      this.slowLoading = false
    },
    // Open the first sheet (Own KPI when there is one)
    selectDefaultSheet () {
      const first = _.flatten(this.navGroups.map(group => group.items))[0]
      this.selectedId = first ? first.id : null
    },
    showSummary (summary) {
      this.summary = summary
      this.status = 'ready'
      this.selectDefaultSheet()
    },
    errorReasonOf (error) {
      const details = error && error.details
      if (details && details.reason) return details.reason
      const code = String(error && error.code)
      if (code.indexOf('permission-denied') !== -1) return 'permission'
      return 'build-failed'
    },
    fetchSummary () {
      const file = this.approvedFile
      if (!file) return
      const meta = file.dataCheckSummary
      const cacheKey = `${this.currentClient}/${file.name}/${meta ? meta.generatedAt : ''}`
      if (summaryCache[cacheKey]) {
        this.showSummary(summaryCache[cacheKey])
        return
      }
      const requestId = ++this.requestId
      this.status = 'loading'
      this.errorReason = null
      this.clearSlowTimer()
      this.slowTimer = setTimeout(() => { this.slowLoading = true }, SLOW_LOADING_MS)
      this.getDataCheckSummary({ fileName: file.name }).then(result => {
        if (requestId !== this.requestId) return
        const summary = Object.freeze(JSON.parse(result.summary))
        summaryCache[`${this.currentClient}/${file.name}/${summary.generatedAt}`] = summary
        this.showSummary(summary)
      }).catch(error => {
        if (requestId !== this.requestId) return
        console.error('Data Check summary error:', error)
        this.errorReason = this.errorReasonOf(error)
        this.status = 'error'
      }).finally(() => {
        if (requestId === this.requestId) this.clearSlowTimer()
      })
    },
    setApproval (approved) {
      if (!this.approvedFile) return
      this.approving = true
      this.setDataCheckApproval({ fileName: this.approvedFile.name, approved: approved }).then(() => {
        this.snackbarColor = 'success'
        this.snackbarText = approved ? this.$t('views.DataCheck.approvedSnackbar') : this.$t('views.DataCheck.pendingSnackbar')
        this.reviewed = false
      }).catch(error => {
        console.error('Data Check approval error:', error)
        this.snackbarColor = 'error'
        this.snackbarText = this.$t('views.DataCheck.approvalError')
      }).finally(() => {
        this.approving = false
        this.snackbar = true
      })
    },
    refresh () {
      if (!this.currentClientDataFilesLoaded) {
        this.status = 'waiting'
      } else if (!this.approvedFile) {
        this.requestId++
        this.clearSlowTimer()
        this.summary = null
        this.status = 'noTemplate'
      } else {
        this.reviewed = false
        this.fetchSummary()
      }
    }
  },
  watch: {
    approvedFileName () {
      this.refresh()
    },
    currentClientDataFilesLoaded () {
      this.refresh()
    }
  },
  created () {
    this.loadClientDataFiles(this.currentClient)
    this.refresh()
  },
  beforeDestroy () {
    this.requestId++
    this.clearSlowTimer()
  },
  components: {
    DataCheckSheetPanel
  }
}
</script>

<style scoped>
.dc-header {
  min-width: 0;
  flex: 1 1 480px;
}
.dc-empty {
  text-align: center;
  padding: 56px 24px;
}
.dc-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.dc-tile {
  flex: 1 1 180px;
  padding: 14px 16px;
}
.dc-tile__label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
}
.dc-tile__value {
  font-size: 1.25rem;
  font-weight: 500;
  margin-top: 6px;
}
.dc-tile__value--nowrap {
  white-space: nowrap;
}
.dc-nav {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
.dc-nav__list {
  position: sticky;
  top: 64px;
}
.dc-nav__header {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.7rem;
  height: 32px;
}
.dc-nav__title {
  white-space: normal;
  line-height: 1.25;
}
.dc-approval {
  gap: 16px;
}
.dc-approval__text {
  flex: 1 1 420px;
}
</style>
